import { ScopeInsertPosition } from "@common/helpers/domains/accounting/chartScopeOrdering";
import {
    ChartEditorAccountRef,
    ChartEditorCreatedAccount,
    ChartEditorMoveNeighbour,
    ChartEditorMoveRow,
} from "@common/schemas/domains/accounting/chart-editor.schema";
import { ChartEditorWireOperation } from "@api/chartEditor.api";

// A create's draft id, mapped to the row the server actually wrote. One id per
// business, because the add fans out one draft row per business.
export type ChartEditorTempIdMap = Map<string, string>;

const withRealId = (accountId: string, tempIds: ChartEditorTempIdMap): string =>
    tempIds.get(accountId) ?? accountId;

const substituteAccountRefs = (
    accounts: ChartEditorAccountRef[],
    tempIds: ChartEditorTempIdMap,
): ChartEditorAccountRef[] =>
    accounts.map((account) => ({
        ...account,
        accountId: withRealId(account.accountId, tempIds),
    }));

const substitutePosition = (
    position: ScopeInsertPosition,
    tempIds: ChartEditorTempIdMap,
): ScopeInsertPosition =>
    position.kind === "afterAccount"
        ? {
              kind: "afterAccount",
              afterAccountId: withRealId(position.afterAccountId, tempIds),
          }
        : position;

const substituteNeighbour = (
    neighbour: ChartEditorMoveNeighbour,
    tempIds: ChartEditorTempIdMap,
): ChartEditorMoveNeighbour =>
    neighbour.kind === "account"
        ? { ...neighbour, accountId: withRealId(neighbour.accountId, tempIds) }
        : neighbour;

const substituteMoveRow = (
    row: ChartEditorMoveRow,
    tempIds: ChartEditorTempIdMap,
): ChartEditorMoveRow => {
    const neighbour = substituteNeighbour(row.neighbour, tempIds);
    return row.kind === "account"
        ? { ...row, neighbour, accountId: withRealId(row.accountId, tempIds) }
        : { ...row, neighbour };
};

// Group paths are never re-derived here: only account ids move from a temp id
// to the id the create returned.
export const substituteChartEditorTempIds = (
    operation: ChartEditorWireOperation,
    tempIds: ChartEditorTempIdMap,
): ChartEditorWireOperation => {
    if (tempIds.size === 0) {
        return operation;
    }
    switch (operation.type) {
        case "updateAccount":
            return {
                type: "updateAccount",
                body: {
                    ...operation.body,
                    accounts: substituteAccountRefs(
                        operation.body.accounts,
                        tempIds,
                    ),
                },
            };
        case "setAccountDisabled":
            return {
                type: "setAccountDisabled",
                body: {
                    ...operation.body,
                    accounts: substituteAccountRefs(
                        operation.body.accounts,
                        tempIds,
                    ),
                },
            };
        case "deleteRows":
            return {
                type: "deleteRows",
                body: {
                    ...operation.body,
                    accounts: substituteAccountRefs(
                        operation.body.accounts,
                        tempIds,
                    ),
                },
            };
        case "addAccount":
            return {
                type: "addAccount",
                body: {
                    ...operation.body,
                    created: operation.body.created.map((row) => ({
                        ...row,
                        position: substitutePosition(row.position, tempIds),
                    })),
                },
            };
        case "addGroup":
            return {
                type: "addGroup",
                body: {
                    ...operation.body,
                    created: operation.body.created.map((row) => ({
                        ...row,
                        position: substitutePosition(row.position, tempIds),
                    })),
                },
            };
        case "move":
            return {
                type: "move",
                body: {
                    rows: operation.body.rows.map((row) =>
                        substituteMoveRow(row, tempIds),
                    ),
                },
            };
        case "renameGroup":
            return operation;
    }
};

// The server ignores `tempId` and does not echo it, so the returned rows are
// matched back to the create by business.
export const recordChartEditorCreatedIds = ({
    operation,
    accounts,
    tempIds,
}: {
    operation: ChartEditorWireOperation;
    accounts: ChartEditorCreatedAccount[];
    tempIds: ChartEditorTempIdMap;
}): void => {
    if (operation.type !== "addAccount") {
        return;
    }
    for (const row of operation.body.created) {
        const created = accounts.find(
            (account) => account.entityId === row.entityId,
        );
        if (created === undefined) {
            throw new Error(`Create response omitted entity ${row.entityId}`);
        }
        tempIds.set(row.tempId, created.id);
    }
};
